import { Alert, List, ListItem, ListItemText, Box, Avatar, Typography, Divider, Paper, Container, Button, TablePagination, Stack, TextField, IconButton, ToggleButton, ToggleButtonGroup, Snackbar, LinearProgress } from "@mui/material";
import ImagesDropzone from "../imagesDropzone";
import { getStorage, ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import AddIcon from '@mui/icons-material/Add';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemIcon from '@mui/material/ListItemIcon';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid2';
import LoadingButton from '@mui/lab/LoadingButton';
import React, { useContext, useEffect, useState } from "react";
import { AuthContext, FireactContext, SetPageTitle } from "../../lib/core";
import { useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { collection, query, where, and, or, getDocs, getDoc, doc, setDoc, addDoc, deleteDoc, GeoPoint, orderBy } from 'firebase/firestore';
import { getAuth, sendEmailVerification } from "firebase/auth";
import CircularProgress from '@mui/material/CircularProgress';
import FolderIcon from '@mui/icons-material/Folder';
import DeleteIcon from '@mui/icons-material/Delete';

import {AdminPageContext} from "./AdminPageContext";

export const AdminInventory = () => {
    const auth = getAuth();
    const { authInstance, firestoreInstance, functionsInstance } = useContext(AuthContext);
    const { config } = useContext(FireactContext);
    const navigate = useNavigate();
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(null);
    const [ prompt, setPrompt ] = useState(null);
    const [processing, setProcessing] = useState(false);
    const { authUser } = useContext(AuthContext);
    const [usersList, setUsersList] = useState(null);
    const [treatments, setTreatments] = useState([]);
    const [treatmentsLoading, setTreatmentsLoading] = useState(false);

    // Add Treatment Dialog State
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [addForm, setAddForm] = useState({ name: '', price: '', description: '', image: '' });
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState([]);
    const [clearPreviewImages, setClearPreviewImages] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [addLoading, setAddLoading] = useState(false);
    const [addError, setAddError] = useState(null);
    const [addSuccess, setAddSuccess] = useState(false);

    // Edit Treatment Dialog State
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editForm, setEditForm] = useState({ id: '', name: '', price: '', description: '', image: '' });
    const [editImageFile, setEditImageFile] = useState(null);
    const [editImagePreview, setEditImagePreview] = useState([]);
    const [editClearPreviewImages, setEditClearPreviewImages] = useState(false);
    const [editUploadProgress, setEditUploadProgress] = useState(0);
    const [editLoading, setEditLoading] = useState(false);
    const [editError, setEditError] = useState(null);
    const [editSuccess, setEditSuccess] = useState(false);

    // Add-ons Dialog State
    const [addonsDialogOpen, setAddonsDialogOpen] = useState(false);
    const [addons, setAddons] = useState([]);
    const [addonsLoading, setAddonsLoading] = useState(false);
    const [addonForm, setAddonForm] = useState({ name: '', description: '', price: '' });
    const [addonError, setAddonError] = useState(null);
    const [addonSuccess, setAddonSuccess] = useState(false);
    const [currentTreatmentId, setCurrentTreatmentId] = useState(null);

    const { admin } = useContext(AdminPageContext);

    // Delete Treatment Dialog State
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    //console.log('authUser ', authUser);
    console.log('admin', admin);

    useEffect(() => {
        let ignore = false;
        const fetchTreatments = async () => {
            if (admin === 'super') {
                setTreatmentsLoading(true);
                try {
                    const q = query(collection(firestoreInstance, 'treatments'), orderBy('name'));
                    const querySnapshot = await getDocs(q);
                    if (!ignore) {
                        setTreatments(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                    }
                } catch (err) {
                    setError('Failed to load treatments.');
                } finally {
                    setTreatmentsLoading(false);
                    setLoaded(true);
                }
            } else {
                setLoaded(true);
            }
        };
        fetchTreatments();
        return () => { ignore = true; };
    }, [auth.currentUser.uid, admin, functionsInstance, firestoreInstance]);
    
    // Fetch add-ons for a treatment
    const fetchAddons = async (treatmentId) => {
        setAddonsLoading(true);
        setAddons([]);
        try {
            const q = collection(firestoreInstance, `treatments/${treatmentId}/add-ons`);
            const querySnapshot = await getDocs(q);
            setAddons(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        } catch (err) {
            setAddonError('Failed to load add-ons.');
        } finally {
            setAddonsLoading(false);
        }
    };

    // Add new add-on
    const handleAddAddon = async () => {
        setAddonError(null);
        if (!addonForm.name || !addonForm.price) {
            setAddonError('Name and price are required.');
            return;
        }
        try {
            const docRef = await addDoc(collection(firestoreInstance, `treatments/${currentTreatmentId}/add-ons`), {
                name: addonForm.name,
                description: addonForm.description,
                price: parseFloat(addonForm.price),
                createdAt: new Date(),
            });
            setAddons(addons => [
                { id: docRef.id, ...addonForm, price: parseFloat(addonForm.price) },
                ...addons
            ]);
            setAddonForm({ name: '', description: '', price: '' });
            setAddonSuccess(true);
            setTimeout(() => setAddonSuccess(false), 1200);
        } catch (err) {
            setAddonError('Failed to add add-on.');
        }
    };

    // Remove add-on
    const handleRemoveAddon = async (addonId) => {
        setAddonError(null);
        try {
            await deleteDoc(doc(firestoreInstance, `treatments/${currentTreatmentId}/add-ons`, addonId));
            setAddons(addons => addons.filter(a => a.id !== addonId));
        } catch (err) {
            setAddonError('Failed to remove add-on.');
        }
    };

    return (
        <>
            {loaded ? (
                <>
                    <Box sx={{ flexGrow: 1 }}>
                        <Grid container spacing={0}>
                            <Grid size={{ xs: 12, md: 12 }}>
                                {error !== null && <Alert severity="error">{error}</Alert>}
                                {prompt !== null && <Alert severity="info">{prompt}</Alert>}
                                {addSuccess && <Snackbar open={addSuccess} autoHideDuration={3000} onClose={()=>setAddSuccess(false)}><Alert severity="success">Treatment added!</Alert></Snackbar>}
                                {addError && <Snackbar open={!!addError} autoHideDuration={4000} onClose={()=>setAddError(null)}><Alert severity="error">{addError}</Alert></Snackbar>}
                            </Grid>
                            <Grid size={{ xs: 12, md: 12 }}>
                                <Box sx={{ flexGrow: 1, p: 0, width: '100%', backgroundColor: '#FFF' }}>
                                    {admin == 'super' && (
                                        <Paper elevation={3} sx={{ p: 3, borderRadius: 3, background: 'linear-gradient(90deg, #e3f2fd 0%, #fff 100%)', boxShadow: 3, mb: 2 }}>
                                            <Stack direction="row" alignItems="center" spacing={2}>
                                                <Avatar sx={{ bgcolor: '#1976d2', color: '#fff', width: 48, height: 48, fontWeight: 700, fontSize: 28 }}>
                                                    <span role="img" aria-label="inventory">📦</span>
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="h4" fontWeight={700} color="#1976d2" sx={{ letterSpacing: 1 }}>
                                                        Treatments Inventory
                                                    </Typography>
                                                    <Typography variant="subtitle1" color="text.secondary">
                                                        Manage and review all available IV and wellness treatments
                                                    </Typography>
                                                    <Button
                                                        variant="contained"
                                                        color="secondary"
                                                        startIcon={<AddIcon />}
                                                        sx={{ ml: 2, mt: 2 }}
                                                        onClick={() => setAddDialogOpen(true)}
                                                    >
                                                        Add Treatment
                                                    </Button>
                                                </Box>
                                            </Stack>
                                        </Paper>
                                    )}
                                    {admin == 'manager' && (
                                        <Box sx={{ height: '70vh', p: 10, alignContent: 'center', textAlign: 'center' }}>
                                            Business Co-operator Access Permission
                                        </Box>
                                    )}
                                    {admin == '' && (
                                        <>
                                            Access Permission is required.
                                        </>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                    {/* Modern Inventory Cards for Treatments */}
                    {admin === 'super' && (
                        <Box sx={{ width: '100%', mt: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                            {treatmentsLoading ? (
                                <CircularProgress />
                            ) : (
                                treatments.length === 0 ? (
                                    <Typography color="text.secondary">No treatments found.</Typography>
                                ) : (
                                    <>
                                        {treatments.map(treatment => (
                                            <Box key={treatment.id} sx={{ width: '100%', mb: 4, display: 'flex', justifyContent: 'center' }}>
                                                <Paper elevation={4} sx={{ width: '100%', maxWidth: 1100, borderRadius: 4, overflow: 'hidden', boxShadow: 6, p: { xs: 1, sm: 2 } }}>
                                                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, width: '100%' }}>
                                                        {/* Left column: image and price */}
                                                        <Box sx={{ width: { xs: '100%', sm: 320 }, minWidth: 220, maxWidth: 380, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 2, background: '#fff' }}>
                                                            <Box sx={{ width: '100%', height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                                                                <img
                                                                    src={treatment.image || 'https://via.placeholder.com/320x180?text=No+Image'}
                                                                    alt={treatment.name || 'Treatment'}
                                                                    style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#fff', display: 'block' }}
                                                                />
                                                            </Box>
                                                            <Typography variant="subtitle1" color="primary" fontWeight={700} sx={{ fontSize: 24 }}>
                                                                {treatment.price ? `$${treatment.price}` : 'No price'}
                                                            </Typography>
                                                        </Box>
                                                        {/* Right column: name, description, actions */}
                                                        <Box sx={{ flex: 1, p: { xs: 2, sm: 3 } }}>
                                                            <Typography variant="h5" fontWeight={700} gutterBottom>
                                                                {treatment.name || 'Untitled'}
                                                            </Typography>
                                                            <Divider sx={{ my: 1, maxWidth: 200 }} />
                                                            <Box sx={{ mb: 2 }}>
                                                                {/* Render HTML description safely */}
                                                                <Typography variant="body1" color="text.secondary" component="div" gutterBottom>
                                                                    <span dangerouslySetInnerHTML={{ __html: treatment.description || '' }} />
                                                                </Typography>
                                                            </Box>
                                                            <Stack direction="row" spacing={2} sx={{ justifyContent: 'flex-start' }}>
                                                                <Button
                                                                    variant="contained"
                                                                    color="primary"
                                                                    onClick={() => {
                                                                        setEditForm({
                                                                            id: treatment.id,
                                                                            name: treatment.name || '',
                                                                            price: treatment.price ? String(treatment.price) : '',
                                                                            description: treatment.description || '',
                                                                            image: treatment.image || ''
                                                                        });
                                                                        setEditImagePreview(treatment.image ? [{ dataURL: treatment.image }] : []);
                                                                        setEditImageFile(null);
                                                                        setEditClearPreviewImages(false);
                                                                        setEditUploadProgress(0);
                                                                        setEditError(null);
                                                                        setEditDialogOpen(true);
                                                                    }}
                                                                    disabled={editLoading}
                                                                >
                                                                    Edit
                                                                </Button>
                                                                <Button
                                                                    variant="outlined"
                                                                    color="info"
                                                                    onClick={async () => {
                                                                        setCurrentTreatmentId(treatment.id);
                                                                        setAddonError(null);
                                                                        setAddonForm({ name: '', description: '', price: '' });
                                                                        await fetchAddons(treatment.id);
                                                                        setAddonsDialogOpen(true);
                                                                    }}
                                                                >
                                                                    Add-ons
                                                                </Button>
                                                                <Button
                                                                    variant="outlined"
                                                                    color="error"
                                                                    onClick={() => {
                                                                        setDeleteTarget(treatment);
                                                                        setDeleteError(null);
                                                                        setDeleteDialogOpen(true);
                                                                    }}
                                                                    disabled={deleteLoading}
                                                                >
                                                                    Delete
                                                                </Button>
                                                            </Stack>
                                                        </Box>
                                                    </Box>
                                                </Paper>
                                            </Box>
                                        ))}
                                        {/* Dialogs outside the map, using state */}
                                        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
                                            <DialogTitle>Delete Treatment</DialogTitle>
                                            <DialogContent>
                                                <DialogContentText>
                                                    Are you sure you want to delete <b>{deleteTarget?.name}</b>? This action cannot be undone.
                                                </DialogContentText>
                                                {deleteError && <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert>}
                                            </DialogContent>
                                            <DialogActions>
                                                <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>Cancel</Button>
                                                <LoadingButton
                                                    onClick={async () => {
                                                        setDeleteLoading(true);
                                                        setDeleteError(null);
                                                        try {
                                                            if (deleteTarget?.image && deleteTarget.image.includes("firebase")) {
                                                                try {
                                                                    const storage = getStorage();
                                                                    const oldImageRef = ref(storage, deleteTarget.image.replace(/^https?:\/\/[^/]+\/o\//, '').replace(/\?.*$/, '').replace(/%2F/g, '/'));
                                                                    await deleteObject(oldImageRef);
                                                                } catch (e) {}
                                                            }
                                                            await deleteDoc(doc(firestoreInstance, 'treatments', deleteTarget.id));
                                                            setTreatments(treatments => treatments.filter(t => t.id !== deleteTarget.id));
                                                            setDeleteDialogOpen(false);
                                                            setDeleteTarget(null);
                                                        } catch (err) {
                                                            setDeleteError('Failed to delete treatment.');
                                                        } finally {
                                                            setDeleteLoading(false);
                                                        }
                                                    }}
                                                    loading={deleteLoading}
                                                    color="error"
                                                    variant="contained"
                                                >
                                                    Delete
                                                </LoadingButton>
                                            </DialogActions>
                                        </Dialog>
                                        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
                                            <DialogTitle>Edit Treatment</DialogTitle>
                                            <DialogContent>
                                                <Stack spacing={2} sx={{ mt: 1 }}>
                                                    <TextField
                                                        label="Name"
                                                        value={editForm.name}
                                                        onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                                                        fullWidth
                                                        required
                                                    />
                                                    <TextField
                                                        label="Price"
                                                        value={editForm.price}
                                                        onChange={e => setEditForm(f => ({ ...f, price: e.target.value.replace(/[^\d.]/g, '') }))}
                                                        fullWidth
                                                        required
                                                        type="number"
                                                    />
                                                    <ImagesDropzone
                                                        setImageList={list => {
                                                            const latest = list && list.length > 0 ? [list[list.length - 1]] : [];
                                                            setEditImagePreview(latest);
                                                            setEditImageFile(latest[0] ? latest[0].file : null);
                                                        }}
                                                        clearPreviewImages={editClearPreviewImages}
                                                        setClearPreviewImages={setEditClearPreviewImages}
                                                    />
                                                    {editUploadProgress > 0 && editUploadProgress < 100 && (
                                                        <LinearProgress variant="determinate" value={editUploadProgress} sx={{ mt: 1 }} />
                                                    )}
                                                    <TextField
                                                        label="Description"
                                                        value={editForm.description}
                                                        onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                                                        fullWidth
                                                        multiline
                                                        minRows={3}
                                                    />
                                                    {editError && <Alert severity="error">{editError}</Alert>}
                                                    {editSuccess && <Alert severity="success">Treatment updated!</Alert>}
                                                </Stack>
                                            </DialogContent>
                                            <DialogActions>
                                                <Button onClick={() => setEditDialogOpen(false)} disabled={editLoading}>Cancel</Button>
                                                <LoadingButton
                                                    onClick={async () => {
                                                        setEditLoading(true);
                                                        setEditError(null);
                                                        try {
                                                            if (!editForm.name || !editForm.price) {
                                                                setEditError('Name and price are required.');
                                                                setEditLoading(false);
                                                                return;
                                                            }
                                                            let imageUrl = editForm.image;
                                                            if (editImageFile) {
                                                                const storage = getStorage();
                                                                if (editForm.image && editForm.image.includes("firebase")) {
                                                                    try {
                                                                        const oldImageRef = ref(storage, editForm.image.replace(/^https?:\/\/[^/]+\/o\//, '').replace(/\?.*$/, '').replace(/%2F/g, '/'));
                                                                        await deleteObject(oldImageRef);
                                                                    } catch (e) {}
                                                                }
                                                                const fileName = `${Date.now()}_${editImageFile.name}`;
                                                                const storageRef = ref(storage, `images/${fileName}`);
                                                                const uploadTask = uploadBytesResumable(storageRef, editImageFile);
                                                                await new Promise((resolve, reject) => {
                                                                    uploadTask.on('state_changed',
                                                                        (snapshot) => {
                                                                            setEditUploadProgress((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
                                                                        },
                                                                        (error) => reject(error),
                                                                        async () => {
                                                                            imageUrl = await getDownloadURL(uploadTask.snapshot.ref);
                                                                            resolve();
                                                                        }
                                                                    );
                                                                });
                                                            }
                                                            const docRef = doc(firestoreInstance, 'treatments', editForm.id);
                                                            await setDoc(docRef, {
                                                                name: editForm.name,
                                                                price: parseFloat(editForm.price),
                                                                description: editForm.description,
                                                                image: imageUrl,
                                                                updatedAt: new Date(),
                                                            }, { merge: true });
                                                            setTreatments(treatments => treatments.map(t => t.id === editForm.id ? { ...t, ...editForm, price: parseFloat(editForm.price), image: imageUrl } : t));
                                                            setEditSuccess(true);
                                                            setTimeout(() => {
                                                                setEditDialogOpen(false);
                                                                setEditSuccess(false);
                                                            }, 1200);
                                                            setEditClearPreviewImages(true);
                                                            setEditUploadProgress(0);
                                                        } catch (err) {
                                                            setEditError('Failed to update treatment.');
                                                        } finally {
                                                            setEditLoading(false);
                                                        }
                                                    }}
                                                    loading={editLoading}
                                                    variant="contained"
                                                    color="primary"
                                                >
                                                    Update
                                                </LoadingButton>
                                            </DialogActions>
                                        </Dialog>
                                    </>
                                )
                            )}
                        </Box>
                    )}
                </>
            ) : (
                <Box sx={{ width: '100%', p: 5, textAlign: 'center' }}><CircularProgress /></Box>
            )}

            {/* Add Treatment Dialog */}
            <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add New Treatment</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Name"
                            value={addForm.name}
                            onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))}
                            fullWidth
                            required
                        />
                        <TextField
                            label="Price"
                            value={addForm.price}
                            onChange={e => setAddForm(f => ({ ...f, price: e.target.value.replace(/[^\d.]/g, '') }))}
                            fullWidth
                            required
                            type="number"
                        />
                        <ImagesDropzone
                            setImageList={list => {
                                // Only keep the latest image
                                const latest = list && list.length > 0 ? [list[list.length - 1]] : [];
                                setImagePreview(latest);
                                setImageFile(latest[0] ? latest[0].file : null);
                            }}
                            clearPreviewImages={clearPreviewImages}
                            setClearPreviewImages={setClearPreviewImages}
                        />
                        {uploadProgress > 0 && uploadProgress < 100 && (
                            <LinearProgress variant="determinate" value={uploadProgress} sx={{ mt: 1 }} />
                        )}
                        <TextField
                            label="Description"
                            value={addForm.description}
                            onChange={e => setAddForm(f => ({ ...f, description: e.target.value }))}
                            fullWidth
                            multiline
                            minRows={3}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddDialogOpen(false)} disabled={addLoading}>Cancel</Button>
                    <LoadingButton
                        onClick={async () => {
                            setAddLoading(true);
                            setAddError(null);
                            try {
                                if (!addForm.name || !addForm.price) {
                                    setAddError('Name and price are required.');
                                    setAddLoading(false);
                                    return;
                                }
                                let imageUrl = '';
                                if (imageFile) {
                                    const storage = getStorage();
                                    const fileName = `${Date.now()}_${imageFile.name}`;
                                    const storageRef = ref(storage, `images/${fileName}`);
                                    const uploadTask = uploadBytesResumable(storageRef, imageFile);
                                    await new Promise((resolve, reject) => {
                                        uploadTask.on('state_changed',
                                            (snapshot) => {
                                                setUploadProgress((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
                                            },
                                            (error) => reject(error),
                                            async () => {
                                                imageUrl = await getDownloadURL(uploadTask.snapshot.ref);
                                                resolve();
                                            }
                                        );
                                    });
                                } else if (addForm.image) {
                                    imageUrl = addForm.image;
                                }
                                const docRef = await addDoc(collection(firestoreInstance, 'treatments'), {
                                    name: addForm.name,
                                    price: parseFloat(addForm.price),
                                    description: addForm.description,
                                    image: imageUrl,
                                    createdAt: new Date(),
                                });
                                setTreatments(treatments => [
                                    { id: docRef.id, ...addForm, price: parseFloat(addForm.price), image: imageUrl },
                                    ...treatments
                                ]);
                                setAddSuccess(true);
                                setAddDialogOpen(false);
                                setAddForm({ name: '', price: '', description: '', image: '' });
                                setImageFile(null);
                                setImagePreview([]);
                                setClearPreviewImages(true);
                                setUploadProgress(0);
                            } catch (err) {
                                setAddError('Failed to add treatment.');
                            } finally {
                                setAddLoading(false);
                            }
                        }}
                        loading={addLoading}
                        variant="contained"
                        color="primary"
                    >
                        Add
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            {/* Add-ons Dialog */}
            <Dialog open={addonsDialogOpen} onClose={() => setAddonsDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Manage Add-ons</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Typography variant="subtitle1">Add a new add-on</Typography>
                        <TextField
                            label="Name"
                            value={addonForm.name}
                            onChange={e => setAddonForm(f => ({ ...f, name: e.target.value }))}
                            fullWidth
                            required
                        />
                        <TextField
                            label="Price"
                            value={addonForm.price}
                            onChange={e => setAddonForm(f => ({ ...f, price: e.target.value.replace(/[^\d.]/g, '') }))}
                            fullWidth
                            required
                            type="number"
                        />
                        <TextField
                            label="Description"
                            value={addonForm.description}
                            onChange={e => setAddonForm(f => ({ ...f, description: e.target.value }))}
                            fullWidth
                            multiline
                            minRows={2}
                        />
                        <Button variant="contained" color="primary" onClick={handleAddAddon} disabled={addonsLoading}>
                            Add Add-on
                        </Button>
                        {addonError && <Alert severity="error">{addonError}</Alert>}
                        {addonSuccess && <Alert severity="success">Add-on added!</Alert>}
                    </Stack>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" sx={{ mb: 1 }}>Current Add-ons</Typography>
                    {addonsLoading ? (
                        <CircularProgress />
                    ) : addons.length === 0 ? (
                        <Typography color="text.secondary">No add-ons found.</Typography>
                    ) : (
                        <List>
                            {addons.map(addon => (
                                <ListItem key={addon.id} secondaryAction={
                                    <IconButton edge="end" color="error" onClick={() => handleRemoveAddon(addon.id)}>
                                        <DeleteIcon />
                                    </IconButton>
                                }>
                                    <ListItemText
                                        primary={`${addon.name} ($${addon.price})`}
                                        secondary={addon.description}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddonsDialogOpen(false)} color="primary">Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
